import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { ActivityFeed } from '../components/feed';
import LivePlayCard from '../components/feed/LivePlayCard';
import { LivePlaysService } from '../generated-api';
import type { LivePlayOutput } from '../generated-api';

const HomePage: React.FC = () => {
  const { playerId } = useSession();
  const [livePlays, setLivePlays] = useState<LivePlayOutput[]>([]);
  const [loadingLivePlays, setLoadingLivePlays] = useState(true);

  useEffect(() => {
    loadLivePlays();
  }, []);

  const loadLivePlays = async () => {
    try {
      const data = await LivePlaysService.getLivePlaysLivePlaysGet({});
      setLivePlays(data);
    } catch (err) {
      console.error('Error loading live plays:', err);
      // Don't show error to user, just leave empty
    } finally {
      setLoadingLivePlays(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="relative mb-12 -mx-4 sm:-mx-6 lg:-mx-8 pt-0 sm:pt-8">
        {/* Hero Image Container */}
        <div className="relative h-[400px] md:h-[450px] lg:h-[500px] overflow-hidden">
          <img
            src="/images/landing_page.png"
            alt="Board Game Night"
            className="w-full h-full object-cover object-center"
          />
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        </div>

        {/* Hero Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 drop-shadow-lg">
            Compete, Track, Conquer
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-6 sm:mb-8 max-w-3xl drop-shadow-md px-4">
            Track every game, challenge your friends, and climb the leaderboard with TrueSkill ratings
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-md sm:max-w-none px-4">
            <Link
              data-track="home_record_play"
              to="/plays/new"
              className="px-6 sm:px-8 py-3 bg-primary-700 text-white font-semibold rounded-lg shadow-lg hover:bg-primary-800 transition-colors duration-200 text-center"
            >
              Record New Play
            </Link>
            <Link
              data-track="home_my_stats"
              to={playerId ? `/players/${playerId}` : "/players"}
              className="px-6 sm:px-8 py-3 bg-surface text-primary-700 font-semibold rounded-lg shadow-lg hover:bg-surface-active transition-colors duration-200 text-center"
            >
              View My Stats
            </Link>
            <Link
              data-track="home_game_tools"
              to="/tools"
              className="px-6 sm:px-8 py-3 bg-white/20 text-white font-semibold rounded-lg shadow-lg backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-colors duration-200 text-center"
            >
              Game Tools
            </Link>
          </div>
        </div>
      </div>

      {/* Live Games Section */}
      {!loadingLivePlays && livePlays.length > 0 && (
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-fluid-h1 font-bold text-heading">Live Games</h2>
            <p className="mt-2 text-body">
              Games currently in progress
            </p>
          </div>
          <div className="space-y-4 max-w-2xl mx-auto">
            {livePlays.map((livePlay) => (
              <LivePlayCard key={livePlay.id} livePlay={livePlay} />
            ))}
          </div>
        </div>
      )}

      {/* Activity Feed Section */}
      <div className="mb-8">
        <h2 className="text-fluid-h1 font-bold text-heading">What's Happening</h2>
        <p className="mt-2 text-body">
          Recent activity from your gaming community
        </p>
      </div>

      <ActivityFeed className="max-w-2xl mx-auto" />
    </div>
  );
};

export default HomePage;
